import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as LinkRoutera, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useServices } from '@/lib/ServicesContext';
import { tresciApi } from '@/lib/api/tresci';
import { SkorkiLista } from './SkorkiLista';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { Karta } from '@/components/Karta';
import { useAuth } from '@/lib/auth/AuthContext';
import {
  base64Pliku, plikMotywuZPaczki, rozpakujMotyw, sprawdzPaczke, wgrajGrafikiPaczki, type WynikPaczki,
} from '@/lib/tresci/paczkaSkorki';
import { OGRANICZENIA, MIN_KONTRAST_POMOCNICZEGO, MIN_KONTRAST_TEKSTU, type Miejsce } from '@/lib/tresci/sezon/kontrakt';
import { kontrast } from '@/lib/tresci/sezon/walidator';
import { HarmonogramOdDo, type WartoscHarmonogramu } from './HarmonogramOdDo';
import { BibliotekaSkorek } from './BibliotekaSkorek';
import { PodgladTelefonu } from './PodgladTelefonu';
import { wymaganySilnik } from '@/lib/tresci/paczkaSkorki';
import { zgodnosc } from '@/lib/tresci/biblioteka';
import { komunikatBledu, useDostepZapisuTresci } from '@/lib/tresci/tresciUi';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * SKÓRKI — PODGLĄD I WALIDACJA PACZKI. BEZ PUBLIKACJI.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Paczka = `motyw.json` (kształt odpowiedzi `/api/app/seasonal-theme`) + pliki grafik. Wszystko
 * dzieje się w przeglądarce operatora — nic nie wychodzi do sieci.
 *
 * Werdykt pochodzi z KOPII silnika aplikacji (R5-10), nie z reguł panelu. Zapis, planowanie,
 * aktywacja, wyłączenie i rollback idą przez serwer CC (`app.content.*`) do API skórek systemu
 * głównego — tylko tam, gdzie serwer CC potwierdzi drogę zapisu (`/stan`).
 */

/** Miejsca, które operator chce zobaczyć w pierwszej kolejności (polecenie OWNERA: HERO, HEADER). */
const NAJPIERW: readonly Miejsce[] = ['EVENT_HERO', 'HEADER_DECORATION'];

function adresPodgladu(plik: File | null): string | null {
  if (!plik || typeof URL.createObjectURL !== 'function') return null;
  return URL.createObjectURL(plik);
}

function Werdykt({ w }: { w: WynikPaczki }) {
  return w.werdykt === 'VALID'
    ? <Chip color="success" label="VALID — paczka zgodna z kontraktem aplikacji" data-testid="werdykt" />
    : <Chip color="error" label="INVALID — nie da się aktywować" data-testid="werdykt" />;
}

function Paleta({ w }: { w: WynikPaczki }) {
  const p = w.motyw?.paleta;
  if (!p) return <Typography variant="body2" sx={{ color: 'text.secondary' }}>Paczka nie zmienia palety ekranu menu.</Typography>;
  const pary: [string, string, string, number][] = [
    ['tekst na tle', p.tekst, p.tlo, MIN_KONTRAST_TEKSTU],
    ['tekst na karcie', p.tekst, p.karta, MIN_KONTRAST_TEKSTU],
    ['pomocniczy na tle', p.pomocniczy, p.tlo, MIN_KONTRAST_POMOCNICZEGO],
  ];
  return (
    <Table size="small" aria-label="Kontrast palety">
      <TableBody>
        {pary.map(([opis, a, b, prog]) => {
          const k = kontrast(a, b);
          return (
            <TableRow key={opis}>
              <TableCell>{opis}</TableCell>
              <TableCell><Box component="span" sx={{ background: b, color: a, px: 1, borderRadius: 1 }}>Aa</Box></TableCell>
              <TableCell>{k.toFixed(2)}:1 (min {prog}:1)</TableCell>
              <TableCell>{k >= prog ? 'OK' : 'ZA NISKI'}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

/**
 * Tryby jednego komponentu (OWNER 11.10 — biblioteka nad nim):
 * - `dodawanie` — „+ Dodaj skórkę": paczka, walidacja, podgląd, nazwa, zapis do biblioteki (szkic);
 * - `rewizje` — szczegóły techniczne: wszystkie rewizje z akcjami i oknem „Kiedy";
 * - `pelny` — oba razem (dotychczasowy ekran; testy kontraktu walidacji i akcji rewizji).
 */
export interface SkorkiZaawansowaneProps {
  tryb?: 'pelny' | 'dodawanie' | 'rewizje';
  /** Zmiana z zewnątrz (biblioteka) — lista rewizji czyta się ponownie. */
  odswiezZewn?: number;
  /** Udany zapis albo akcja rewizji — biblioteka czyta się ponownie. */
  onZmiana?: () => void;
}

export function SkorkiZaawansowane({ tryb = 'pelny', odswiezZewn = 0, onZmiana }: SkorkiZaawansowaneProps) {
  const auth = useAuth();
  const mozePublikowac = auth.has('app.content.publish');

  const [wynik, setWynik] = useState<WynikPaczki | null>(null);
  const [sprawdzanie, setSprawdzanie] = useState(false);
  // Pliki paczki zostają w pamięci strony: przy zapisie grafiki idą na serwer (OWNER 11.10).
  const [plikiPaczki, setPlikiPaczki] = useState<File[]>([]);
  const [zapisywanie, setZapisywanie] = useState(false);
  const [harmonogram, setHarmonogram] = useState<WartoscHarmonogramu | null>(null);
  const onHarmonogram = useCallback((w: WartoscHarmonogramu) => setHarmonogram(w), []);
  const { api } = useServices();
  const mozeEdytowac = auth.has('app.content.write');
  // Surowy `motyw.json` — to on idzie do systemu głównego; walidator aplikacji daje werdykt i powody.
  const [surowy, setSurowy] = useState<Record<string, unknown> | null>(null);
  const [nazwa, setNazwa] = useState('');
  const [komunikat, setKomunikat] = useState<{ ok: boolean; tekst: string } | null>(null);
  const [odswiez, setOdswiez] = useState(0);
  const zapis = useDostepZapisuTresci();

  const onPliki = async (lista: FileList | null) => {
    if (!lista || lista.length === 0) return;
    setSprawdzanie(true);
    try {
      const pliki = Array.from(lista);
      setPlikiPaczki(pliki);
      setWynik(await sprawdzPaczke(pliki));
      const motyw = plikMotywuZPaczki(pliki);
      try {
        const sparsowany: unknown = motyw ? rozpakujMotyw(JSON.parse(await motyw.text())) : null;
        setSurowy(sparsowany && typeof sparsowany === 'object' && !Array.isArray(sparsowany) ? sparsowany as Record<string, unknown> : null);
      } catch {
        setSurowy(null);
      }
    } finally {
      setSprawdzanie(false);
    }
  };

  const okno = {
    startsAt: harmonogram?.start ? harmonogram.start.toISOString() : null,
    endsAt: harmonogram?.koniec && !harmonogram.bezKonca ? harmonogram.koniec.toISOString() : null,
    bezKonca: harmonogram?.bezKonca ?? false,
    ok: harmonogram?.ok ?? false,
  };

  const zapiszSzkic = async () => {
    if (!surowy || !wynik) return;
    setZapisywanie(true);
    try {
      // Najpierw grafiki: paczka idzie do systemu głównego już z adresami HTTPS serwera, inaczej
      // aplikacja szukałaby ich w sobie i pokazała zwykły wygląd. Błąd wgrania = nic nie zapisane.
      const { paczka, wgrane } = await wgrajGrafikiPaczki(surowy, plikiPaczki, async (plik) =>
        (await tresciApi.wgrajZasobSkorki(api, plik.name, await base64Pliku(plik))).url);
      const s = await tresciApi.utworzSkorke(api, {
        name: nazwa.trim() || wynik.motyw?.nazwa || 'Skórka',
        package: paczka,
        clientValidation: { valid: wynik.werdykt === 'VALID', reasons: wynik.bledy },
      });
      setKomunikat({
        ok: s.effectiveStatus !== 'ERROR',
        tekst: s.effectiveStatus === 'ERROR'
          ? `Zapisano rewizję #${s.id} jako BŁĄD — nie da się jej aktywować: ${s.validationErrors.join('; ')}`
          : `Zapisano szkic #${s.id} (rewizja ${s.revision})`
            + (wgrane.length ? `, grafiki na serwerze: ${wgrane.length} (${wgrane.join(', ')}).` : '.'),
      });
      // Pole nazwy wraca do pustego po zapisie (historyczny TEST: zostawała poprzednia nazwa
      // i następny szkic dostawał ją po cichu). Po błędzie dane formularza zostają.
      setNazwa('');
      setOdswiez((x) => x + 1);
      onZmiana?.();
    } catch (e) {
      setKomunikat({ ok: false, tekst: komunikatBledu(e) });
    } finally {
      setZapisywanie(false);
    }
  };

  // Adresy obrazków z plików lokalnych — zwalniane przy zmianie paczki, żeby nie zostawiać wycieków.
  const podglady = useMemo(() => {
    const m = new Map<string, string>();
    for (const z of wynik?.zasoby ?? []) {
      const adres = z.stan === 'OK' ? adresPodgladu(z.plik) : null;
      if (adres) m.set(z.id, adres);
    }
    return m;
  }, [wynik]);
  useEffect(() => () => { for (const a of podglady.values()) URL.revokeObjectURL?.(a); }, [podglady]);

  const dekoracje = [...(wynik?.motyw?.dekoracje ?? [])].sort(
    (a, b) => (NAJPIERW.includes(a.miejsce) ? 0 : 1) - (NAJPIERW.includes(b.miejsce) ? 0 : 1),
  );

  // Podgląd na telefonie z plików LOKALNYCH (przed wgraniem) — adres z paczki → plik o tej nazwie.
  const rozwiazLokalnie = useCallback((url: string): string | null => {
    if (/^https:\/\//i.test(url)) return url;
    const nazwaPliku = decodeURIComponent((url.split(/[?#]/)[0] ?? '').split('/').pop() ?? '');
    const z = wynik?.zasoby.find((x) => x.nazwaPliku === nazwaPliku);
    return z ? podglady.get(z.id) ?? null : null;
  }, [wynik, podglady]);
  const pokazDodawanie = tryb !== 'rewizje';
  const pokazRewizje = tryb !== 'dodawanie';
  const zgodnoscPaczki = surowy && wynik?.motyw ? zgodnosc(wymaganySilnik(surowy)) : null;

  return (
    <Box>
      {tryb === 'pelny' ? (
        <NaglowekEkranu
          tytul="Skórki"
          opis="Paczka skórki sprawdzana tak samo, jak sprawdzi ją aplikacja ucznia. Wszystko lokalnie — nic nie idzie do sieci."
        />
      ) : null}

      <Stack spacing={3}>
        {pokazDodawanie ? (<>
        <Karta tytul="Paczka">
          <Button variant="outlined" component="label" disabled={sprawdzanie}>
            {sprawdzanie ? 'Sprawdzam…' : 'Wybierz pliki paczki (motyw.json + grafiki)'}
            <input
              hidden
              type="file"
              multiple
              accept=".json,.svg,.png,.gif,.webp"
              aria-label="Pliki paczki skórki"
              onChange={(e) => { void onPliki(e.target.files); }}
            />
          </Button>

          {wynik ? (
            <Box sx={{ mt: 2 }}>
              <Werdykt w={wynik} />
              {wynik.motyw ? (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {wynik.motyw.nazwa || wynik.motyw.id} · id <code>{wynik.motyw.id}</code> · rewizja {wynik.motyw.rewizja}
                </Typography>
              ) : null}
              {wynik.bledy.length ? (
                <Alert severity="error" role="alert" sx={{ mt: 2 }}>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>{wynik.bledy.map((b) => <li key={b}>{b}</li>)}</ul>
                </Alert>
              ) : null}
              {wynik.ostrzezenia.length ? (
                <Alert severity="warning" role="status" sx={{ mt: 2 }}>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>{wynik.ostrzezenia.map((o) => <li key={o}>{o}</li>)}</ul>
                </Alert>
              ) : null}
            </Box>
          ) : null}
        </Karta>

        {wynik?.zasoby.length ? (
          <Karta tytul="Zasoby">
            <Table size="small" aria-label="Zasoby paczki">
              <TableHead>
                <TableRow><TableCell>Zasób</TableCell><TableCell>Plik</TableCell><TableCell>Rodzaj</TableCell><TableCell>Stan</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {wynik.zasoby.map((z) => (
                  <TableRow key={z.id}>
                    <TableCell>{z.id}</TableCell>
                    <TableCell>{z.nazwaPliku}</TableCell>
                    <TableCell>{z.rodzaj}</TableCell>
                    <TableCell>{z.stan}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Karta>
        ) : null}

        {dekoracje.length ? (
          <Karta tytul="Miejsca w aplikacji">
            <Stack spacing={2}>
              {dekoracje.map((d) => {
                const og = OGRANICZENIA[d.miejsce];
                const adres = podglady.get(d.zasob.id);
                return (
                  <Box key={d.miejsce} data-testid={`miejsce-${d.miejsce}`}>
                    <Typography variant="subtitle2">
                      {d.miejsce} · zasób <code>{d.zasob.id}</code> · animacja {d.preset}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      limit miejsca {og.maxSzerokosc}×{og.maxWysokosc} px{og.overlay ? ' · nakładka' : ''}
                    </Typography>
                    <Box sx={{
                      mt: 1, width: og.maxSzerokosc, maxWidth: '100%', height: og.maxWysokosc,
                      border: '1px dashed', borderColor: 'divider', display: 'grid', placeItems: 'center', overflow: 'hidden',
                    }}>
                      {adres
                        ? <img src={adres} alt={`${d.miejsce}: ${d.zasob.id}`} style={{ maxWidth: '100%', maxHeight: '100%' }} />
                        : <Typography variant="caption">podgląd niedostępny</Typography>}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Karta>
        ) : null}

        {wynik?.motyw ? (
          <Karta tytul="Paleta ekranu menu">
            <Paleta w={wynik} />
          </Karta>
        ) : null}

        {surowy && wynik?.motyw && tryb === 'dodawanie' ? (
          <Karta tytul="Podgląd na telefonie">
            {zgodnoscPaczki ? (
              <Typography variant="body2" sx={{ mb: 1 }} data-testid="zgodnosc-paczki">
                {zgodnoscPaczki.etykieta}{zgodnoscPaczki.ostrzezenie ? ` — ${zgodnoscPaczki.ostrzezenie}` : ''}
              </Typography>
            ) : null}
            <PodgladTelefonu paczka={surowy} rozwiaz={rozwiazLokalnie} />
          </Karta>
        ) : null}

        {wynik?.motyw?.pasek ? (
          <Karta tytul="Tekst okazji w motywie">
            <Typography variant="body2">✦ {wynik.motyw.pasek.tekst}</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Komunikat z „Pasek główny" ma pierwszeństwo przed tym tekstem.
            </Typography>
          </Karta>
        ) : null}

        </>) : null}

        {pokazRewizje ? (
          <Karta tytul="Kiedy">
            <HarmonogramOdDo onZmiana={onHarmonogram} wylaczone={!auth.has('app.content.write')} etykietaGrupy="Harmonogram skórki" />
          </Karta>
        ) : null}

        {mozeEdytowac && pokazDodawanie ? (
          <Karta tytul="Zapis">
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
              <TextField size="small" label="Nazwa skórki" value={nazwa} onChange={(e) => setNazwa(e.target.value)} />
              <Button variant="outlined" disabled={!surowy || !wynik || !zapis.dostepny || zapisywanie} onClick={() => { void zapiszSzkic(); }}>{zapisywanie ? 'Wgrywam grafiki i zapisuję…' : 'Zapisz szkic'}</Button>
            </Stack>
            {zapis.powod ? (
              <Alert severity="warning" role="status" data-testid="powod-braku-zapisu" sx={{ mt: 1 }}>{zapis.powod}</Alert>
            ) : null}
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
              Skórka trafia do biblioteki jako szkic; grafiki z paczki idą na serwer przy zapisie. Paczka z błędem zapisuje
              się jako BŁĄD — nie pojawi się w bibliotece jako gotowa do publikacji i nie da się jej aktywować.
            </Typography>
            {komunikat ? (
              <Alert severity={komunikat.ok ? 'success' : 'error'} role={komunikat.ok ? 'status' : 'alert'} data-testid="wynik-zapisu" sx={{ mt: 1 }}>
                {komunikat.tekst}
              </Alert>
            ) : null}
          </Karta>
        ) : null}

        {pokazRewizje ? (
          <Karta tytul="Rewizje skórek">
            <SkorkiLista odswiez={odswiez + odswiezZewn} mozePublikowac={mozePublikowac} zapisDostepny={zapis.dostepny} okno={okno}
              onZmiana={onZmiana} />
          </Karta>
        ) : null}
      </Stack>
    </Box>
  );
}

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * TREŚCI APLIKACJI → SKÓRKI — BIBLIOTEKA (OWNER 11.10)
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * Na górze to, co teraz i co dalej; niżej skórki do wyboru (miniatura, podgląd, „Zaplanuj").
 * Paczka (JSON) nie jest podstawowym interfejsem: „+ Dodaj skórkę" otwiera dotychczasowe wgrywanie,
 * a pełny dotychczasowy ekran (wszystkie rewizje z akcjami, ręczne okno) zostaje pod „Szczegóły techniczne"
 * (`#/tresci?widok=techniczny`) — nic z istniejących akcji nie znika.
 */
export function SkorkiPage() {
  const [parametry] = useSearchParams();
  if (parametry.get('widok') === 'techniczny') {
    return (
      <Box>
        <Button component={LinkRoutera} to="/tresci" size="small" sx={{ mb: 1 }}>← Biblioteka skórek</Button>
        <SkorkiZaawansowane tryb="pelny" />
      </Box>
    );
  }
  return <BibliotekaZDodawaniem />;
}

function BibliotekaZDodawaniem() {
  const auth = useAuth();
  const [odswiezBiblioteke, setOdswiezBiblioteke] = useState(0);
  const [dodawanie, setDodawanie] = useState(false);
  const poZmianieNizej = useCallback(() => setOdswiezBiblioteke((x) => x + 1), []);

  return (
    <Box>
      <NaglowekEkranu
        tytul="Skórki"
        opis="Wybierz skórkę z biblioteki, sprawdź podgląd i zaplanuj termin (czas warszawski). Aplikacja pokaże ją sama w tym okresie."
      />
      <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end', mb: 2 }}>
        <Button component={LinkRoutera} to="/tresci?widok=techniczny" size="small">Szczegóły techniczne: wszystkie rewizje</Button>
        {auth.has('app.content.write') ? (
          <Button variant="contained" onClick={() => setDodawanie(true)}>+ Dodaj skórkę</Button>
        ) : null}
      </Stack>

      <BibliotekaSkorek odswiez={odswiezBiblioteke} />

      <Dialog open={dodawanie} onClose={() => setDodawanie(false)} maxWidth="md" fullWidth aria-labelledby="dodaj-skorke-tytul">
        <DialogTitle id="dodaj-skorke-tytul">Dodaj skórkę do biblioteki</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Wybierz pliki paczki (motyw.json i grafiki). Paczka zostanie sprawdzona tak jak w aplikacji; zobaczysz podgląd,
            nadasz nazwę i zapiszesz skórkę w bibliotece jako szkic.
          </Typography>
          <SkorkiZaawansowane tryb="dodawanie" onZmiana={poZmianieNizej} />
        </DialogContent>
        <DialogActions><Button onClick={() => setDodawanie(false)}>Zamknij</Button></DialogActions>
      </Dialog>
    </Box>
  );
}

